import dashboardAPI from './index'
import progressiveLoaderAll from './progressiveLoaderAll'
import insuranceAPI from '@/core/services/apiInsurance/dashboard'
import { generateBlocks, combineBlocks } from '@/core/services/apiInsurance/dashboard/progressiveLoader'
import { monthsBetween } from '@/core/utils/dashboardDates'
import {
  MIN_BLOCK_DURATION_MS,
  runStaggered,
  runWithMinDuration,
} from '@/core/utils/requestPacing'

function aborted() {
  return new DOMException('Aborted', 'AbortError')
}

function throwIfAborted(signal) {
  if (signal?.aborted) throw aborted()
}

/**
 * Cuerpo de `insurance/dashboard/get-data`.
 * El dashboard de seguro usa la misma lectura: `response.data` o el cuerpo directo.
 */
async function fetchInsurance(payload, signal) {
  const response = await insuranceAPI.getData(payload, { signal })
  const data = response?.data ?? response
  if (!data || typeof data !== 'object' || !data.average) {
    throw new Error(
      `Invalid insurance dashboard response for ${payload.start_date} - ${payload.end_date}`
    )
  }
  return data
}

function payloadFor(block, facilities, userId) {
  const payload = {
    start_date: block.start,
    end_date: block.end,
    facilities,
  }
  if (userId) payload.user_id = userId
  return payload
}

/** Misma normalización que un mes del loader del dashboard total, antes de sumar. */
function unifyCashBlock(response) {
  const data = progressiveLoaderAll.unifyAllResponse(response)
  progressiveLoaderAll.normalizeAverages(data)
  progressiveLoaderAll.normalizePatientSeries(data)
  progressiveLoaderAll.normalizeMemberships(data)
  progressiveLoaderAll.normalizeBonus(data)
  return data
}

function combineCash(rawResponses, dateRange) {
  const unified = rawResponses.map(unifyCashBlock)
  return progressiveLoaderAll.combineMonthlyData(unified, dateRange)
}

/**
 * Pide el total y el seguro en la misma tanda.
 * Si un lado falla, ese lado no sigue y no se combina a medias.
 * Cancelar corta los dos.
 */
async function fetchSides(payload, signal, state) {
  throwIfAborted(signal)

  const jobs = []
  if (!state.cashError) {
    jobs.push({
      side: 'cash',
      run: () => dashboardAPI.getAllDashboardData(payload, { signal }),
    })
  }
  if (!state.insuranceError) {
    jobs.push({
      side: 'insurance',
      run: () => fetchInsurance(payload, signal),
    })
  }
  if (!jobs.length) return

  const results = await runStaggered(
    jobs.map(job => async () => {
      try {
        return { side: job.side, ok: true, value: await job.run() }
      } catch (error) {
        if (error?.name === 'AbortError') throw error
        return { side: job.side, ok: false, error }
      }
    })
  )

  results.forEach(result => {
    if (!result.ok) {
      if (result.side === 'cash') state.cashError = result.error
      else state.insuranceError = result.error
      return
    }
    if (result.side === 'cash') state.cashResponses.push(result.value)
    else state.insuranceResponses.push(result.value)
  })
}

function emptyState() {
  return {
    cashError: null,
    insuranceError: null,
    cashResponses: [],
    insuranceResponses: [],
  }
}

function packResult(state, { start, end, expected, unifyCash }) {
  const cashReady = !state.cashError && state.cashResponses.length === expected
  const insuranceReady =
    !state.insuranceError && state.insuranceResponses.length === expected

  return {
    cash: cashReady
      ? unifyCash
        ? combineCash(state.cashResponses, { start, end })
        : state.cashResponses[0]
      : null,
    insurance: insuranceReady
      ? expected === 1
        ? state.insuranceResponses[0]
        : combineBlocks(state.insuranceResponses, { start, end })
      : null,
    cashError: state.cashError,
    insuranceError: state.insuranceError,
  }
}

/**
 * Carga el dashboard total y las gráficas de seguro con el mismo filtro.
 * Con varios bloques, los dos van en una sola pasada de 2 meses.
 * Las gráficas solo reciben el resultado ya combinado.
 */
export async function loadAllWithInsurance({
  start,
  end,
  facilities,
  userId = null,
  signal,
  onProgress,
  useProgressive = false,
} = {}) {
  throwIfAborted(signal)

  // Un mes o menos sigue en una sola respuesta cruda, igual que el loader del total.
  // Varios meses usan los mismos bloques de 2 meses que el dashboard de seguro.
  const splitByMonth = useProgressive && monthsBetween(start, end) > 1
  const blocks = splitByMonth ? generateBlocks(start, end) : [{ start, end }]
  const state = emptyState()

  if (!splitByMonth) {
    await fetchSides(payloadFor({ start, end }, facilities, userId), signal, state)
    return packResult(state, { start, end, expected: 1, unifyCash: false })
  }

  for (let i = 0; i < blocks.length; i++) {
    throwIfAborted(signal)
    const block = blocks[i]
    if (onProgress) {
      onProgress({
        current: i + 1,
        total: blocks.length,
        percentage: Math.min(99, Math.round(((i + 1) / blocks.length) * 100)),
        currentMonth: block.month,
        currentYear: block.year,
      })
    }

    const isLast = i === blocks.length - 1
    await runWithMinDuration(
      () => fetchSides(payloadFor(block, facilities, userId), signal, state),
      isLast ? 0 : MIN_BLOCK_DURATION_MS
    )
  }

  throwIfAborted(signal)
  return packResult(state, {
    start,
    end,
    expected: blocks.length,
    unifyCash: true,
  })
}
